// react-query wrappers around the api() fetch helper.
//
// Keep these small + co-located with each page where possible. The
// generic shape (key, fn, options) doesn't really add safety here —
// these hooks pin the response shape with TypeScript so call sites
// don't have to remember it.

import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export interface MeResponse {
  tenant: string;
  user: {
    id: string | null;
    role: string | null;
    email: string | null;
    displayName: string | null;
    avatarUrl: string | null;
  };
  plan_slug: string;
  entitlements: Record<string, unknown>;
}

export function useMe() {
  return useQuery<MeResponse>({
    queryKey: ['me'],
    queryFn: () => api<MeResponse>('/api/me'),
  });
}

export interface Podcast {
  id: string;
  tenant: string;
  title: string;
  slug: string;
  description: string | null;
  episodeCount?: number;
  createdAt: string;
}

export function usePodcasts() {
  return useQuery<{ podcasts: Podcast[]; total: number }>({
    queryKey: ['podcasts'],
    queryFn: () => api('/api/podcasts'),
  });
}

export interface Episode {
  id: string;
  tenant: string;
  podcastId: string;
  title: string;
  slug: string;
  episodeNumber: number | null;
  status: 'draft' | 'scheduled' | 'published';
  createdAt: string;
}

export function useEpisodes(podcastId: string | null) {
  return useQuery<{ episodes: Episode[]; total: number }>({
    queryKey: ['episodes', podcastId],
    queryFn: () => api(`/api/episodes?podcastId=${encodeURIComponent(podcastId!)}`),
    enabled: !!podcastId,
  });
}

export interface Project {
  id: string;
  tenant: string;
  name: string;
  episodeId: string | null;
  sampleRate: number;
  createdAt: string;
}

export function useProjects() {
  return useQuery<{ projects: Project[]; total: number }>({
    queryKey: ['projects'],
    queryFn: () => api('/api/projects'),
  });
}

export interface Job {
  id: string;
  tenant: string;
  jobType: string;
  status: 'pending' | 'active' | 'completed' | 'failed';
  progress: number;
  createdAt: string;
}

export function useJobs() {
  return useQuery<{ jobs: Job[]; page: number; limit: number }>({
    queryKey: ['jobs'],
    queryFn: () => api('/api/jobs'),
    // Polling — jobs go through pending → active → completed quickly
    // enough that 5s is a good UX trade.
    refetchInterval: 5000,
  });
}
